import axios from 'axios';
import { getTokenPayloadSafely } from "../App";

const getAuthToken = () => {
  try {
    const tokenData = sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
    if (!tokenData) {
      console.warn("No auth token found in sessionStorage or localStorage");
      return null;
    }
    try {
      const parsed = JSON.parse(tokenData);
      return parsed.access || parsed.access_token || parsed.token;
    } catch {
      // Token is a raw JWT string, not JSON
      return tokenData;
    }
  } catch (e) {
    console.error("Error retrieving auth token:", e);
  }
  return null;
};

/**
 * Check if a token is expired based on the JWT exp claim
 * @param token JWT token string
 * @param gracePeriod Buffer time in seconds before actual expiration (default: 60 seconds)
 * @returns true if token is expired or invalid
 */
const isTokenExpired = (token: string | null, gracePeriod: number = 60): boolean => {
  if (!token || typeof token !== "string") {
    console.warn("Token is null or not a string");
    return true;
  }

  const payload = getTokenPayloadSafely(token);
  if (!payload || !payload.exp) {
    console.warn("Token payload is missing or has no exp claim");
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
  const expirationTime = payload.exp;
  const timeUntilExpiration = expirationTime - currentTime;

  if (timeUntilExpiration <= 0) {
    console.warn(`Token has expired (expired ${Math.abs(timeUntilExpiration)} seconds ago)`);
    return true;
  }

  if (timeUntilExpiration <= gracePeriod) {
    console.warn(`Token will expire in ${timeUntilExpiration} seconds (within ${gracePeriod}s grace period)`);
    return true;
  }

  console.log(`Token is valid for ${timeUntilExpiration} seconds`);
  return false;
};

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

// Request interceptor - adds auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      // Check if token is expired before sending request
      if (isTokenExpired(token)) {
        console.error('Cannot make request: Token is expired or invalid');
        // Clear stored token to prevent further requests
        sessionStorage.removeItem("authToken");
        localStorage.removeItem("authToken");
        // Throw error to be caught by error interceptor
        throw new Error('Token expired');
      }
      
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      
      // Only set Content-Type for non-FormData requests
      // Let browser set it automatically for FormData (multipart/form-data)
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }
      
      // Log request details for debugging (in development)
      if (process.env.NODE_ENV === 'development') {
        console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
        console.log('[API Headers]', config.headers);
        console.log('[API Token]', token.substring(0, 20) + '...');
      }
    } else {
      console.warn('No authentication token available for request to:', config.url);
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handles token errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const errorData = error.response?.data;
    
    // Handle token-related errors
    if (status === 401 || status === 403) {
      console.error('[API Error] Authentication error:', {
        status,
        message: error.message,
        data: errorData,
        url: error.config?.url
      });
      
      // Clear stored tokens
      sessionStorage.removeItem("authToken");
      localStorage.removeItem("authToken");
      
      if (errorData?.detail === "Given token not valid for any token type" || 
          errorData?.message?.includes("token_not_valid") ||
          error.message?.includes("token_not_valid")) {
        console.error('Token validation failed on server side');
        return Promise.reject(new Error('token_not_valid'));
      }
      
      if (status === 401) {
        return Promise.reject(new Error('Unauthorized'));
      }
      if (status === 403) {
        return Promise.reject(new Error('Forbidden'));
      }
    }
    
    if (status === 500) {
      console.error('[API Error] Server error:', {
        status,
        url: error.config?.url,
        data: errorData
      });
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
export { getAuthToken, isTokenExpired };

